import { Calendar, Clock, Star, Tag, Trash2, User } from "lucide-react";
import { DateTime } from "luxon";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { PriceDisplay } from "@/components/ui/price-display";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import type { AppointmentListFragment, PointRewardFragment } from "@/gql/graphql";
import { useCurrentAbility } from "@/lib/authorization";
import { timezoneUtils } from "@/lib/timezone";

interface CustomerAppointmentHistoryTableProps {
  appointments: AppointmentListFragment[];
  pointRewards?: PointRewardFragment[];
  loading?: boolean;
  onAppointmentClick?: (appointmentId: string) => void;
  onLoadMore?: () => Promise<any>;
  hasMore?: boolean;
  onDeleteAppointment?: (appointmentId: string) => void;
}

export function CustomerAppointmentHistoryTable({
  appointments,
  pointRewards: _pointRewards = [],
  loading,
  onAppointmentClick,
  onLoadMore,
  hasMore = false,
  onDeleteAppointment,
}: CustomerAppointmentHistoryTableProps) {
  const { t } = useTranslation("customers");
  const { t: tAppt } = useTranslation("appointments");
  const [showAll, setShowAll] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const _ability = useCurrentAbility();
  const canDelete = false; // TODO: enable when delete appointment feature is ready
  if (loading) {
    return (
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center gap-2">
            <Calendar className="w-5 h-5" />
            <CardTitle className="text-lg">{t("detail.appointmentHistory.title")}</CardTitle>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {[...Array(5)].map((_, i) => (
              <div key={`skeleton-${i}`} className="animate-pulse">
                <div className="h-12 bg-muted rounded" />
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  const sortedAppointments = [...appointments].sort(
    (a, b) =>
      timezoneUtils.fromISO(b.startTime).toMillis() - timezoneUtils.fromISO(a.startTime).toMillis()
  );
  const displayedAppointments = showAll ? sortedAppointments : sortedAppointments.slice(0, 10);

  return (
    <Card>
      <CardHeader className="pb-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Calendar className="w-5 h-5" />
            <CardTitle className="text-lg">{t("detail.appointmentHistory.title")}</CardTitle>
          </div>
          {appointments.length > 0 && (
            <Badge variant="secondary" className="text-xs">
              {appointments.length} {t("detail.appointmentHistory.total")}
            </Badge>
          )}
        </div>
      </CardHeader>
      <CardContent>
        {displayedAppointments.length > 0 ? (
          <div className="space-y-4">
            <div className="border rounded-lg overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[140px]">
                      {t("detail.appointmentHistory.headers.dateTime")}
                    </TableHead>
                    <TableHead>{t("detail.appointmentHistory.headers.services")}</TableHead>
                    <TableHead>{t("detail.appointmentHistory.headers.staff")}</TableHead>
                    <TableHead className="w-[80px]">
                      {t("detail.appointmentHistory.headers.duration")}
                    </TableHead>
                    <TableHead className="w-[100px] text-right">
                      {t("detail.appointmentHistory.headers.price")}
                    </TableHead>
                    <TableHead className="w-[110px] text-center">
                      {tAppt("paymentMethod")}
                    </TableHead>
                    <TableHead className="w-[80px] text-right">
                      {t("detail.appointmentHistory.headers.points")}
                    </TableHead>
                    {canDelete && <TableHead className="w-[50px]">Action</TableHead>}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {displayedAppointments.map((appointment) => {
                    const startTime = timezoneUtils.fromISO(appointment.startTime);
                    const services = appointment.appointmentServices || [];
                    const totalDuration = services.reduce(
                      (sum, service) => sum + (service.serviceDuration || 0),
                      0
                    );

                    const earnedPoints = 0;

                    return (
                      <TableRow
                        key={appointment.id}
                        className="hover:bg-muted/50 cursor-pointer"
                        onClick={() => onAppointmentClick?.(appointment.id)}
                      >
                        <TableCell>
                          <div className="text-sm">
                            <div className="font-medium">
                              {startTime.toLocaleString(DateTime.DATE_MED)}
                            </div>
                            <div className="text-muted-foreground flex items-center gap-1">
                              <Clock className="w-3 h-3" />
                              {startTime.toLocaleString(DateTime.TIME_SIMPLE)}
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="space-y-1">
                            {services.length > 0 ? (
                              services.map((service, index) => (
                                <div key={service.id} className="flex items-center gap-2">
                                  <Tag className="w-3 h-3 text-muted-foreground flex-shrink-0" />
                                  <span className="font-medium text-sm">{service.serviceName}</span>
                                  {services.length > 1 && (
                                    <Badge variant="outline" className="text-xs px-1 py-0">
                                      {index + 1}
                                    </Badge>
                                  )}
                                </div>
                              ))
                            ) : (
                              <div className="flex items-center gap-2">
                                <Tag className="w-3 h-3 text-muted-foreground" />
                                <span className="text-sm text-muted-foreground">
                                  {t("detail.appointmentHistory.noServices")}
                                </span>
                              </div>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="space-y-1">
                            {services.length > 0 ? (
                              // Show unique staff members
                              Array.from(
                                new Set(services.map((s) => s.worker?.user?.id).filter(Boolean))
                              ).map((userId) => {
                                const worker = services.find((s) => s.worker?.user?.id === userId)
                                  ?.worker?.user;
                                return worker ? (
                                  <div key={userId} className="flex items-center gap-2">
                                    <User className="w-3 h-3 text-muted-foreground flex-shrink-0" />
                                    <span className="text-sm">
                                      {worker.firstName} {worker.lastName}
                                    </span>
                                  </div>
                                ) : null;
                              })
                            ) : (
                              <span className="text-sm text-muted-foreground">—</span>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <span className="text-sm">{totalDuration}min</span>
                        </TableCell>
                        <TableCell className="text-right">
                          <span className="text-sm font-medium">
                            <PriceDisplay amount={appointment.totalPrice} />
                          </span>
                        </TableCell>
                        <TableCell className="text-center">
                          <Badge variant="outline" className="text-xs">
                            {tAppt(`paymentMethod_${appointment.paymentMethod ?? "NONE"}`)}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-1">
                            {earnedPoints > 0 ? (
                              <>
                                <Star className="w-3 h-3 text-amber-500 fill-current" />
                                <span className="text-sm font-medium text-amber-600">
                                  {earnedPoints}
                                </span>
                              </>
                            ) : (
                              <span className="text-sm text-muted-foreground">—</span>
                            )}
                          </div>
                        </TableCell>
                        {canDelete && (
                          <TableCell onClick={(e) => e.stopPropagation()}>
                            <AlertDialog>
                              <AlertDialogTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-7 w-7 text-muted-foreground hover:text-destructive hover:bg-destructive/10"
                                >
                                  <Trash2 className="h-3.5 w-3.5" />
                                </Button>
                              </AlertDialogTrigger>
                              <AlertDialogContent>
                                <AlertDialogHeader>
                                  <AlertDialogTitle>Delete appointment?</AlertDialogTitle>
                                  <AlertDialogDescription>
                                    This will permanently delete this appointment. This action
                                    cannot be undone.
                                  </AlertDialogDescription>
                                </AlertDialogHeader>
                                <AlertDialogFooter>
                                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                                  <AlertDialogAction
                                    className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                                    onClick={() => onDeleteAppointment?.(appointment.id)}
                                  >
                                    Delete
                                  </AlertDialogAction>
                                </AlertDialogFooter>
                              </AlertDialogContent>
                            </AlertDialog>
                          </TableCell>
                        )}
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>

            {appointments.length > 10 && (
              <div className="text-center pt-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={async () => {
                    if (!showAll && hasMore && onLoadMore) {
                      setIsLoadingMore(true);
                      try {
                        await onLoadMore();
                        setShowAll(true);
                      } finally {
                        setIsLoadingMore(false);
                      }
                    } else {
                      setShowAll(!showAll);
                    }
                  }}
                  disabled={isLoadingMore}
                >
                  {showAll
                    ? "Show Less"
                    : `${t("detail.appointmentHistory.viewAll")} (${appointments.length} ${t("detail.appointmentHistory.appointments")})`}
                </Button>
              </div>
            )}
          </div>
        ) : (
          <div className="text-center py-8">
            <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
              <Calendar className="w-8 h-8 text-muted-foreground" />
            </div>
            <h3 className="text-sm font-medium text-foreground mb-1">
              {t("detail.appointmentHistory.empty.title")}
            </h3>
            <p className="text-sm text-muted-foreground">
              {t("detail.appointmentHistory.empty.description")}
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
